import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { extensionsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';
import ResetPasswordModal from '../../components/ResetPasswordModal';

const ExtensionsPage = () => {
  const [extensions, setExtensions] = useState([]);
  const [filteredExtensions, setFilteredExtensions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, extension: null });
  const [resetModal, setResetModal] = useState({ open: false, extension: null });

  useEffect(() => {
    fetchExtensions();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      setFilteredExtensions(
        extensions.filter(
          (ext) =>
            ext.extension?.toString().includes(query) ||
            ext.name?.toLowerCase().includes(query) ||
            ext.email?.toLowerCase().includes(query)
        )
      );
    } else {
      setFilteredExtensions(extensions);
    }
  }, [searchQuery, extensions]);

  const fetchExtensions = async () => {
    try {
      const response = await extensionsAPI.getAll();
      const data = response.data?.data || response.data || [];
      setExtensions(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch extensions:', error);
      toast.error('Failed to load extensions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.extension) return;

    try {
      // Use extension_id for VitalPBX API
      await extensionsAPI.delete(deleteModal.extension.extension_id);
      toast.success('Extension deleted successfully');
      setDeleteModal({ open: false, extension: null });
      fetchExtensions();
    } catch (error) {
      console.error('Failed to delete extension:', error);
      toast.error('Failed to delete extension');
    }
  };

  const handleResetPassword = async (newPassword) => {
    if (!resetModal.extension) return;

    try {
      // Use extension_id for VitalPBX API
      await extensionsAPI.resetPassword(resetModal.extension.extension_id, newPassword);
      toast.success('Password reset successfully');
      setResetModal({ open: false, extension: null });
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password');
    }
  };

  // Helper function to determine device status
  const getDeviceStatus = (ext) => {
    if (ext.devices && ext.devices.length > 0) {
      return { label: 'Active', class: 'badge-success' };
    }
    return { label: 'No Devices', class: 'badge-warning' };
  };

  // Helper function to determine voicemail status
  const getVoicemailStatus = (ext) => {
    if (ext.mailbox && ext.mailbox.length > 0) {
      return { label: 'Enabled', class: 'badge-info' };
    }
    return { label: 'Disabled', class: 'badge-warning' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Extensions</h1>
          <p className="text-slate-400 mt-1">
            Manage phone extensions for your organization
          </p>
        </div>
        <Link to="/extensions/new" className="btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Extension
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input
          type="text"
          placeholder="Search by extension, name, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input pl-10 max-w-md"
        />
      </div>

      {/* Extensions Table */}
      {filteredExtensions.length > 0 ? (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Extension</th>
                <th>Name</th>
                <th>Email</th>
                <th>Devices</th>
                <th>Voicemail</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExtensions.map((ext) => {
                const deviceStatus = getDeviceStatus(ext);
                const voicemailStatus = getVoicemailStatus(ext);
                
                return (
                  <tr key={ext.extension_id}>
                    <td>
                      <span className="font-mono font-medium text-primary-400">
                        {ext.extension}
                      </span>
                    </td>
                    <td className="text-white font-medium">
                      {ext.name || '-'}
                    </td>
                    <td className="text-slate-400">{ext.email || '-'}</td>
                    <td>
                      <span className={`badge ${deviceStatus.class}`}>
                        {deviceStatus.label}
                        {ext.devices && ext.devices.length > 0 && ` (${ext.devices.length})`}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${voicemailStatus.class}`}>
                        {voicemailStatus.label}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setResetModal({ open: true, extension: ext })}
                          className="p-2 text-slate-400 hover:text-yellow-400 hover:bg-slate-800 rounded-lg transition-colors"
                          title="Reset Password"
                        >
                          <KeyIcon className="w-4 h-4" />
                        </button>
                        <Link
                          to={`/extensions/${ext.extension_id}/edit`}
                          className="p-2 text-slate-400 hover:text-primary-400 hover:bg-slate-800 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setDeleteModal({ open: true, extension: ext })}
                          className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-800 flex items-center justify-center">
            <PlusIcon className="w-8 h-8 text-slate-600" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No extensions found</h3>
          <p className="text-slate-400 mb-4">
            {searchQuery
              ? 'Try adjusting your search terms'
              : 'Get started by adding your first extension'}
          </p>
          {!searchQuery && (
            <Link to="/extensions/new" className="btn-primary inline-flex">
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Extension
            </Link>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, extension: null })}
        onConfirm={handleDelete}
        title="Delete Extension"
        message={`Are you sure you want to delete extension ${deleteModal.extension?.extension}? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={resetModal.open}
        onClose={() => setResetModal({ open: false, extension: null })}
        onConfirm={handleResetPassword}
        extensionNumber={resetModal.extension?.extension}
      />
    </div>
  );
};

export default ExtensionsPage;
